"use client";

import { useRef, useState, useCallback, useEffect } from "react";
import { cn } from "~/lib/utils";
import { Button } from "./button";
import { UploadCloudIcon, FileIcon, XIcon } from "~/components/icons";

export interface FileDropzoneProps {
	accept?: string;
	maxSizeMB?: number;
	onFileSelect?: (file: File) => void;
	onFileRemove?: () => void;
	className?: string;
	disabled?: boolean;
	value?: File | null;
	hideDropzoneWhenFileSelected?: boolean;
}

export function FileDropzone({
	accept = ".csv",
	maxSizeMB = 10,
	onFileSelect,
	onFileRemove,
	className,
	disabled = false,
	value,
	hideDropzoneWhenFileSelected = false,
}: FileDropzoneProps) {
	const [isDragging, setIsDragging] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(
		value ?? null,
	);
	const [error, setError] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (value === null && selectedFile !== null) {
			setSelectedFile(null);
			setError(null);
			if (inputRef.current) {
				inputRef.current.value = "";
			}
		} else if (value !== undefined) {
			setSelectedFile(value);
		}
	}, [value, selectedFile]);

	const validateFile = useCallback(
		(file: File): boolean => {
			setError(null);

			const acceptedTypes = accept
				.split(",")
				.map((t) => t.trim().toLowerCase());
			const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
			const mimeType = file.type.toLowerCase();

			const isValidType = acceptedTypes.some(
				(type) =>
					type === fileExtension ||
					type === mimeType ||
					(type.startsWith(".") && fileExtension === type),
			);

			if (!isValidType) {
				setError(`Invalid file type. Please upload a ${accept} file.`);
				return false;
			}

			const maxSizeBytes = maxSizeMB * 1024 * 1024;
			if (file.size > maxSizeBytes) {
				setError(`File too large. Maximum size is ${maxSizeMB}MB.`);
				return false;
			}

			return true;
		},
		[accept, maxSizeMB],
	);

	const handleFile = useCallback(
		(file: File) => {
			if (validateFile(file)) {
				setSelectedFile(file);
				onFileSelect?.(file);
			}
		},
		[validateFile, onFileSelect],
	);

	const handleDragOver = useCallback(
		(e: React.DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			e.stopPropagation();
			if (!disabled) {
				setIsDragging(true);
			}
		},
		[disabled],
	);

	const handleDragLeave = useCallback(
		(e: React.DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			e.stopPropagation();
			setIsDragging(false);
		},
		[],
	);

	const handleDrop = useCallback(
		(e: React.DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			e.stopPropagation();
			setIsDragging(false);

			if (disabled) return;

			const files = e.dataTransfer.files;
			const firstFile = files?.[0];
			if (firstFile) {
				handleFile(firstFile);
			}
		},
		[disabled, handleFile],
	);

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const files = e.target.files;
			const firstFile = files?.[0];
			if (firstFile) {
				handleFile(firstFile);
			}
		},
		[handleFile],
	);

	const handleButtonClick = useCallback(() => {
		inputRef.current?.click();
	}, []);

	const handleRemoveFile = useCallback(() => {
		setSelectedFile(null);
		setError(null);
		if (inputRef.current) {
			inputRef.current.value = "";
		}
		onFileRemove?.();
	}, [onFileRemove]);

	const shouldShowDropzone = !hideDropzoneWhenFileSelected || !selectedFile;

	return (
		<div className={cn("w-full", className)}>
			{shouldShowDropzone && (
				<div
					className={cn(
						"relative flex flex-col items-center justify-center gap-6 rounded-[10px] border border-dashed px-14 py-12 transition-colors",
						isDragging
							? "border-[#003261] bg-[rgba(0,50,97,0.04)]"
							: "border-[rgba(0,0,0,0.25)] bg-transparent",
						disabled && "cursor-not-allowed opacity-50",
						!disabled && "cursor-pointer",
					)}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
					onClick={disabled ? undefined : handleButtonClick}
				>
					<input
						ref={inputRef}
						type="file"
						accept={accept}
						onChange={handleInputChange}
						className="hidden"
						disabled={disabled}
					/>

					{/* Upload Cloud Icon */}
					<UploadCloudIcon
						className="text-[#00071b]"
						width={48}
						height={48}
					/>

					{/* Description */}
					<div className="flex flex-col items-center gap-3">
						<p className="text-sm font-medium leading-5 text-[#1c2024]">
							Select a file or drag and drop here
						</p>
						<p className="text-xs leading-4 tracking-[0.04px] text-[#60646c]">
							CSV file size no more than {maxSizeMB}MB
						</p>
					</div>

					{/* Select File Button */}
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={(e) => {
							e.stopPropagation();
							handleButtonClick();
						}}
						disabled={disabled}
						className="border-[rgba(0,89,175,0.36)] bg-[rgba(0,85,199,0.04)] text-sm font-semibold text-[rgba(0,50,97,0.7)] hover:bg-[rgba(0,85,199,0.08)]"
					>
						Select File
					</Button>
				</div>
			)}

			{/* Selected File Display */}
			{selectedFile && !error && (
				<div
					className={cn(
						"flex items-center justify-between rounded-md border border-[#d9d9e0] bg-[#f9f9fb] px-4 py-3",
						!shouldShowDropzone && "mt-0",
					)}
				>
					<div className="flex items-center gap-3">
						<FileIcon className="h-5 w-5 text-[#003261]" />
						<div className="flex flex-col">
							<p className="text-sm font-medium text-[#1c2024]">
								{selectedFile.name}
							</p>
							<p className="text-xs text-[#60646c]">
								{(selectedFile.size / 1024).toFixed(1)} KB
							</p>
						</div>
					</div>
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							handleRemoveFile();
						}}
						className="flex h-8 w-8 items-center justify-center rounded-md text-[#60646c] hover:bg-[rgba(0,0,47,0.05)] hover:text-[#1c2024]"
						aria-label="Remove file"
					>
						<XIcon className="h-4 w-4" />
					</button>
				</div>
			)}

			{/* Error Message */}
			{error && <p className="mt-2 text-sm text-red-600">{error}</p>}
		</div>
	);
}
